//
// Copyright (c) 2011-2022 by Curalate, Inc.
//
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  getEngagementStats,
  getEngagementVideos,
} from '@apis/tiktok-analytics/tiktokAnalyticsApi';
import {
  IGetEngagementVideosRequest,
  IMaterializedTikTokVideoWithMetrics,
  ITikTokEngagementDailyStats,
  ITikTokEngagementMetrics,
  TikTokMetricSortKey,
} from '@apis/tiktok-analytics/tiktokAnalyticsApiModel';
import {
  getAliasedDateRange,
  IAliasedDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import TimeZoneId from '@models/TimeZoneId';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import {
  TikTokEngagementPerformanceOption,
  TOP_MEDIA_DEFAULT_LIMIT,
} from '@tiktok/components/analytics/engagement/tiktokEngagementConstants';
import { fillInMissingEngagementDates } from '@tiktok/components/analytics/engagement/tiktokEngagementUtils';
import {
  getTikTokEngagementDateRange,
  getTikTokEngagementMediaPerformanceIsOrderAscending,
  getTikTokEngagementMediaPerformanceLimit,
  getTikTokEngagementMediaPerformanceOffset,
  getTikTokEngagementMediaPerformanceSortKey,
} from '@tiktok/redux/engagement/selectors/getTikTokEngagement';
import {
  ITikTokEngagementState,
  ITikTokMediaPerformanceState,
  TikTokTopMediaView,
} from '@tiktok/redux/engagement/tiktokEngagementModel';
import {
  getTikTokUserDisplayName,
  getTikTokUserId,
  getTikTokUsername,
} from '@tiktok/redux/user/selectors/getTikTokUser';
import { isDefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

import { crl8 } from 'app/curalate.globals';

const initialState: ITikTokEngagementState = {
  dateRange: getAliasedDateRange(
    'last7Days',
    crl8.session?.timeZone.id ?? TimeZoneId.Utc,
  ),
  dailyStats: [],
  summaryStats: undefined,
  currentGraphMetric: TikTokEngagementPerformanceOption.Views,
  mediaPerformance: {
    isOrderAscending: false,
    limit: TOP_MEDIA_DEFAULT_LIMIT,
    media: [],
    offset: 0,
    sortKey: TikTokMetricSortKey.PostedTimestamp,
    total: 0,
    view: TikTokTopMediaView.TableView,
  },
  flags: {
    isFetchingEngagementStats: false,
    isFetchingTopMedia: false,
  },
  errors: {
    fetchEngagementStatsError: undefined,
    fetchTopMediaError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.TikTokEngagement,
  initialState,
  reducers: {
    updateDateRange(state, action: PayloadAction<IAliasedDateRange>) {
      state.dateRange = action.payload;
    },
    updateTopMediaDisplayState(
      state,
      action: PayloadAction<Partial<ITikTokMediaPerformanceState>>,
    ) {
      state.mediaPerformance.sortKey =
        action.payload.sortKey ?? state.mediaPerformance.sortKey;
      state.mediaPerformance.view =
        action.payload.view ?? state.mediaPerformance.view;
      state.mediaPerformance.offset =
        action.payload.offset ?? state.mediaPerformance.offset;
      state.mediaPerformance.limit =
        action.payload.limit ?? state.mediaPerformance.limit;
      state.mediaPerformance.isOrderAscending =
        action.payload.isOrderAscending ??
        state.mediaPerformance.isOrderAscending;
    },
    updateGraphMetric(
      state,
      action: PayloadAction<TikTokEngagementPerformanceOption>,
    ) {
      state.currentGraphMetric = action.payload;
    },
    requestEngagementStats(state) {
      state.flags.isFetchingEngagementStats = true;
      state.errors.fetchEngagementStatsError = undefined;
    },
    receiveEngagementStatsSucceeded(
      state,
      action: PayloadAction<{
        dailyStats: ITikTokEngagementDailyStats[];
        summaryStats: ITikTokEngagementMetrics;
      }>,
    ) {
      state.flags.isFetchingEngagementStats = false;
      state.dailyStats = action.payload.dailyStats;
      state.summaryStats = action.payload.summaryStats;
    },
    receiveEngagementStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingEngagementStats = false;
      state.errors.fetchEngagementStatsError = action.payload;
    },
    requestUserMedia(state) {
      state.flags.isFetchingTopMedia = true;
      state.errors.fetchTopMediaError = undefined;
    },
    receiveUserMediaSucceeded(
      state,
      action: PayloadAction<{
        videos: IMaterializedTikTokVideoWithMetrics[];
        total: number;
      }>,
    ) {
      state.flags.isFetchingTopMedia = false;
      state.mediaPerformance.media = [...action.payload.videos];
      state.mediaPerformance.total = action.payload.total;
    },
    receiveUserMediaFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingTopMedia = false;
      state.errors.fetchTopMediaError = action.payload;
    },
  },
});

export const changeDateRangeAndRefreshContent = createThunk(
  (dateRange: IAliasedDateRange) => (dispatch) => {
    dispatch(actions.updateDateRange(dateRange));

    void dispatch(fetchEngagementMetrics(dateRange));
    void dispatch(fetchEngagementUserMedia());
  },
);

export const updateTopMediaFiltersAndFetchContent = createThunk(
  (sortKey: TikTokMetricSortKey) => (dispatch, getState) => {
    const currentSort = getState().tiktok.engagement.mediaPerformance.sortKey;
    const currentIsOrderAscending =
      getState().tiktok.engagement.mediaPerformance.isOrderAscending;

    // If we are re-sorting by the same key, invert the sort direction, otherwise default to descending
    const isOrderAscending =
      currentSort === sortKey ? !currentIsOrderAscending : false;

    dispatch(
      updateTopMediaDisplayState({
        sortKey,
        offset: 0, // Since we are fetching new content, return to the first page of results
        isOrderAscending,
      }),
    );

    void dispatch(fetchEngagementUserMedia());
  },
);

export const fetchEngagementMetrics = createThunk(
  (dateRange: IAliasedDateRange) => async (dispatch, getState) => {
    const tiktokUserId = getState().tiktok.user.user?.id;
    const tiktokUsername = getTikTokUsername(getState());
    const tiktokDisplayName = getTikTokUserDisplayName(getState());
    const userTimeZoneId = getUserTimeZone(getState());

    // If either the tiktokUserId or tiktokUsername is undefined, do not attempt to fetch user media
    if (!isDefined(tiktokUserId) || !isDefined(tiktokUsername)) {
      return;
    }

    dispatch(actions.requestEngagementStats());

    try {
      const statsResponse = await getEngagementStats({
        tiktokUsername,
        tiktokDisplayName,
        tiktokUserId,
        startTime: dateRange.startDate,
        endTime: dateRange.endDate,
      });

      const statsData = {
        dailyStats: fillInMissingEngagementDates(
          statsResponse.dailyMetrics,
          dateRange.startDate,
          dateRange.endDate,
          userTimeZoneId,
        ),
        summaryStats: statsResponse.summaryMetrics,
      };

      dispatch(actions.receiveEngagementStatsSucceeded(statsData));
    } catch (e) {
      dispatch(
        actions.receiveEngagementStatsFailed(
          getRequestErrorMessage(e) ??
            'An unknown error eccurred when attempting to fetch TikTok engagement metrics',
        ),
      );
    }
  },
);

export const fetchEngagementUserMedia = createThunk(
  () => async (dispatch, getState) => {
    const state = getState();
    const dateRange = getTikTokEngagementDateRange(state);
    const isOrderAscending =
      getTikTokEngagementMediaPerformanceIsOrderAscending(state);
    const limit = getTikTokEngagementMediaPerformanceLimit(state);
    const offset = getTikTokEngagementMediaPerformanceOffset(state);
    const sortKey = getTikTokEngagementMediaPerformanceSortKey(state);
    const tiktokUserId = getTikTokUserId(state);

    const tiktokUsername = getTikTokUsername(state);
    const tiktokDisplayName = getTikTokUserDisplayName(state);

    // If either the tiktokUserId or tiktokUsername is undefined, do not attempt to fetch user media
    if (!isDefined(tiktokUserId) || !isDefined(tiktokUsername)) {
      return;
    }

    void dispatch(
      fetchUserMedia({
        tiktokUsername,
        tiktokDisplayName,
        tiktokUserId,
        startTime: dateRange.startDate,
        endTime: dateRange.endDate,
        limit,
        offset,
        sortKey,
        isOrderAscending,
      }),
    );
  },
);

export const fetchUserMedia = createThunk(
  (req: IGetEngagementVideosRequest) => async (dispatch) => {
    dispatch(actions.requestUserMedia());

    try {
      const mediaData = await getEngagementVideos(req);

      dispatch(actions.receiveUserMediaSucceeded(mediaData));
    } catch (e) {
      dispatch(
        actions.receiveUserMediaFailed(
          getRequestErrorMessage(e) ??
            'An unknown error eccurred when attempting to fetch TikTok user media',
        ),
      );
    }
  },
);

export const { updateTopMediaDisplayState, updateGraphMetric } = actions;

export default reducer;
